import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Wand2, Send, CalendarRange, CircleAlert, FileDown, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const de = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "");
const CW = 27;
const CHIP = {
  accepted: "bg-[#0a788c] text-white", proposed: "bg-[#0a788c]/15 text-[#075f70] ring-1 ring-[#0a788c]/40",
  completed: "bg-slate-900 text-white", signed: "bg-slate-900 text-white",
  draft: "bg-amber-100 text-amber-900 ring-1 ring-amber-400", approved: "bg-amber-500 text-white",
  sent: "bg-violet-500 text-white", conflict: "bg-rose-600 text-white", change_requested: "bg-rose-100 text-rose-800 ring-1 ring-rose-400",
  no_show: "bg-rose-200 text-rose-900", cancelled_late: "bg-rose-200 text-rose-900",
};
const DOT = {
  accepted: "bg-[#0a788c]", proposed: "bg-[#0a788c]/40 border border-[#0a788c]", completed: "bg-slate-900", signed: "bg-slate-900",
  draft: "bg-amber-400", approved: "bg-amber-600", sent: "bg-violet-500", conflict: "bg-rose-600", change_requested: "bg-rose-400",
  no_show: "bg-rose-200", cancelled_late: "bg-rose-200",
};
const hhmm = (t) => (!t ? "" : t.endsWith(":00") ? String(Number(t.slice(0, 2))) : `${Number(t.slice(0, 2))}:${t.slice(3, 5)}`);
const SLABEL = {
  accepted: "Bestätigt", proposed: "Geplant", completed: "Durchgeführt", signed: "Unterschrieben", draft: "Entwurf – Freigabe nötig",
  approved: "Freigegeben – noch nicht gesendet", sent: "Gesendet – Bestätigung offen", conflict: "Konflikt", change_requested: "Änderungswunsch",
  no_show: "Nicht erschienen", cancelled_late: "Kurzfristig abgesagt",
};
const BG = { past: "bg-slate-50", off: "bg-slate-100", holiday: "bg-slate-100", away: "bg-slate-100", full: "bg-rose-50/70", free: "bg-white", today: "bg-amber-50/60", blocked: "bg-slate-300/60" };
const ymAdd = (ym, n) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

function Legend() {
  const keys = ["accepted", "sent", "approved", "draft", "completed", "conflict"];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-600" data-testid="overview-legend">
      {keys.map((k) => <span key={k} className="flex items-center gap-1.5"><i className={`inline-block w-2.5 h-2.5 rounded-full ${DOT[k]}`} />{SLABEL[k]}</span>)}
      <span className="flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 rounded-sm ring-2 ring-rose-500" />Außerhalb meiner Arbeitszeit</span>
      <span className="flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 bg-rose-50 border border-rose-200" />Tag voll belegt</span>
      <span className="flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 bg-slate-300" />Gesperrt (Urlaub/Schulung)</span>
      <span className="flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 bg-slate-100" />Frei/Feiertag</span>
      <span className="text-slate-500">Zahl im Feld = Uhrzeit (Beginn)</span>
    </div>
  );
}

export default function PlannerOverview({ coachId, refreshKey, onChange }) {
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [edit, setEdit] = useState("");
  const [win, setWin] = useState({ from: "", to: "" });
  const load = () => {
    const params = { start: `${ym}-01`, months, ...(coachId ? { coach_id: coachId } : {}) };
    api.get("/planner/overview", { params }).then((r) => setData(r.data)).catch(err);
  };
  useEffect(() => { load(); }, [ym, months, coachId, refreshKey]); // eslint-disable-line

  const days = useMemo(() => (data?.months || []).flatMap((m) => m.days), [data]);
  const maxDay = useMemo(() => {
    const w = data?.work;
    if (!w) return 3;
    const mins = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    return Math.max(1, Math.floor((mins(w.end) - mins(w.start)) / 120));
  }, [data]);
  const fullRuns = useMemo(() => {
    const out = [];
    (data?.weeks || []).filter((w) => !w.past).forEach((w) => {
      const last = out[out.length - 1];
      if (w.state === "blocked") out.push({ state: "blocked", from_kw: w.kw, to_kw: w.kw, label: w.block_label });
      else if (w.state === "full") { if (last && last.to_kw === w.kw - 1 && last.state === "full") last.to_kw = w.kw; else out.push({ state: "full", from_kw: w.kw, to_kw: w.kw }); }
      else if (w.state === "tight") out.push({ state: "tight", from_kw: w.kw, to_kw: w.kw, room: w.free_days });
    });
    return out;
  }, [data]);

  const plan = async (clientId) => {
    setBusy(clientId);
    try {
      const { data: d } = await api.post("/planner/run", { coach_id: coachId || undefined, client_ids: [clientId] });
      toast.success(`${d.drafts.length} Terminvorschläge erstellt`);
      load(); onChange?.();
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const send = async (clientId, name) => {
    if (!window.confirm(`Alle Entwürfe von ${name} freigeben und als Einladung senden?`)) return;
    setBusy(clientId);
    try {
      const { data: d } = await api.post(`/planner/client/${clientId}/approve-send`, null, { params: coachId ? { coach_id: coachId } : {} });
      toast.success(`${d.approved} Termine freigegeben · Einladung ${d.test_mode ? "(Testmodus)" : "gesendet"}${d.conflicts?.length ? ` · ${d.conflicts.length} Konflikt(e)` : ""}`);
      load(); onChange?.();
    } catch (e) { err(e); } finally { setBusy(""); }
  };

  const toggleWeek = async (w) => {
    const p = coachId ? { coach_id: coachId } : {};
    try {
      if (w.state === "blocked") {
        if (!window.confirm(`Sperre für KW ${w.kw}${w.block_label ? ` (${w.block_label})` : ""} aufheben?`)) return;
        await api.delete(`/planner/blocks/group/${w.block_group}`, { params: p });
        toast.success(`KW ${w.kw} wieder frei`);
      } else {
        const label = window.prompt(`KW ${w.kw} komplett sperren – Grund?`, "Urlaub");
        if (label === null) return;
        const to = new Date(`${w.week_start}T00:00:00Z`);
        to.setUTCDate(to.getUTCDate() + 4);
        const { data: res } = await api.post("/planner/blocks/range", { date_from: w.week_start, date_to: to.toISOString().slice(0, 10), label: label || "Gesperrt" }, { params: p });
        toast.success(`KW ${w.kw} gesperrt (${res.days} Tage)` + (res.drafts_cancelled ? ` · ${res.drafts_cancelled} Vorschläge entfernt` : "")
          + (res.appointments_affected ? ` · Achtung: ${res.appointments_affected} bestätigte Termine liegen in dieser Woche` : ""));
      }
      load(); onChange?.();
    } catch (e) { err(e); }
  };

  const pdf = async () => {
    try {
      const r = await api.get("/planner/overview/pdf", { params: { start: `${ym}-01`, months, ...(coachId ? { coach_id: coachId } : {}) }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `Kundenuebersicht_${ym}_${months}M.pdf`;
      a.click();
      toast.success("PDF erstellt");
    } catch (e) { err(e); }
  };

  const saveWindow = async (clientId) => {
    try {
      await api.put(`/planner/client/${clientId}/window`, { date_from: win.from || null, date_to: win.to || null }, { params: coachId ? { coach_id: coachId } : {} });
      toast.success("Zeitraum gespeichert – es wird nur noch innerhalb dieses Zeitraums geplant");
      setEdit(""); load(); onChange?.();
    } catch (e) { err(e); }
  };

  if (!data) return <div className="border border-slate-200 bg-white px-5 py-6 text-xs text-slate-400 mb-8">Übersicht lädt…</div>;
  const width = days.length * CW;

  return (
    <div className="border border-slate-200 bg-white mb-8" data-testid="planner-overview">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5"><CalendarRange size={12} /> Kundenübersicht</div>
          <div className="text-sm text-slate-700 mt-1">Alle Teilnehmer mit Uhrzeit je Termin · meine Zeiten {data.work?.start}–{data.work?.end} · max. {maxDay} Termine/Tag</div>
        </div>
        <div className="flex items-center gap-1.5">
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} className="h-9 border border-slate-300 bg-white px-2 text-xs" data-testid="overview-months-select">
            <option value={1}>1 Monat</option>
            <option value={2}>2 Monate</option>
            <option value={3}>3 Monate</option>
            <option value={6}>6 Monate</option>
          </select>
          <button onClick={() => setYm(ymAdd(ym, -1))} className="h-9 w-9 border border-slate-300 flex items-center justify-center hover:bg-slate-100" data-testid="overview-prev"><ChevronLeft size={15} /></button>
          <button onClick={() => setYm(new Date().toISOString().slice(0, 7))} className="h-9 px-3 border border-slate-300 text-xs hover:bg-slate-100" data-testid="overview-today">Heute</button>
          <button onClick={() => setYm(ymAdd(ym, 1))} className="h-9 w-9 border border-slate-300 flex items-center justify-center hover:bg-slate-100" data-testid="overview-next"><ChevronRight size={15} /></button>
          <button onClick={pdf} className="h-9 px-3 border border-slate-300 text-xs hover:bg-slate-100 flex items-center gap-1.5" data-testid="overview-pdf-btn"><FileDown size={14} /> PDF</button>
        </div>
      </div>

      {(fullRuns.length > 0 || data.outside_count > 0) && (
        <div className="px-5 py-3 border-b border-slate-100 bg-amber-50/60 text-xs text-amber-900 space-y-1" data-testid="overview-capacity-hints">
          {data.outside_count > 0 && (
            <div className="flex items-start gap-1.5 text-rose-800" data-testid="overview-outside-hint">
              <CircleAlert size={13} className="mt-0.5 shrink-0" />
              <span><b>{data.outside_count} Termin(e)</b> liegen außerhalb meiner Arbeitszeit ({data.work?.start}–{data.work?.end}) – rot umrandet in der Übersicht</span>
            </div>
          )}
          {fullRuns.slice(0, 6).map((r, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <CircleAlert size={13} className="mt-0.5 shrink-0" />
              {r.state === "blocked"
                ? <span><b>KW {r.from_kw}</b> gesperrt{r.label ? ` – ${r.label}` : ""} – hier wird nichts geplant</span>
                : r.state === "full"
                ? <span><b>{r.from_kw === r.to_kw ? `KW ${r.from_kw}` : `KW ${r.from_kw}–${r.to_kw}`}</b> komplett belegt – hier passt kein neuer Termin mehr rein</span>
                : <span><b>KW {r.from_kw}</b> fast voll – noch Platz für {r.room === 1 ? "einen Kunden" : `${r.room} Kunden`} ({r.room === 1 ? "1 freier Tag" : `${r.room} freie Tage`})</span>}
            </div>
          ))}
        </div>
      )}

      {data.weeks.some((w) => !w.past) && (
        <div className="px-5 py-4 border-b border-slate-100" data-testid="overview-weekload">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2">Meine Termine je Woche <span className="font-normal normal-case tracking-normal text-slate-400">· Woche anklicken zum Sperren (Urlaub, Schulung)</span></div>
          <div className="flex items-end gap-1 overflow-x-auto">
            {data.weeks.filter((w) => !w.past).map((w) => {
              const max = Math.max(4, ...data.weeks.filter((x) => !x.past).map((x) => x.booked));
              const blocked = w.state === "blocked";
              const tone = blocked ? "bg-slate-400" : w.state === "full" ? "bg-rose-500" : w.state === "tight" ? "bg-amber-400" : "bg-[#0a788c]";
              return (
                <button key={w.week_start} onClick={() => toggleWeek(w)} className="flex flex-col items-center shrink-0 group/wk" style={{ width: 30 }}
                  title={blocked ? `KW ${w.kw} gesperrt${w.block_label ? ` – ${w.block_label}` : ""} · klicken zum Aufheben` : `KW ${w.kw}: ${w.booked} Termine · ${w.free_days} freie Tage · klicken zum Sperren`}
                  data-testid={`overview-week-${w.kw}`}>
                  <div className="text-[9px] text-slate-500 font-mono h-3">{blocked ? <Lock size={9} className="mx-auto" /> : w.booked || ""}</div>
                  <div className="w-full bg-slate-100 flex items-end group-hover/wk:ring-1 group-hover/wk:ring-slate-400" style={{ height: 44 }}>
                    <div className={`w-full ${tone} transition-all`} style={{ height: blocked ? "100%" : `${Math.round((w.booked / max) * 100)}%` }} />
                  </div>
                  <div className="text-[9px] text-slate-500 mt-1">{w.kw}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <div style={{ minWidth: 170 + width }}>
          <div className="flex border-b border-slate-100 sticky top-0 bg-white z-20">
            <div className="w-[170px] sm:w-[300px] shrink-0 sticky left-0 bg-white z-10 px-4 py-2 text-[10px] font-bold uppercase tracking-overline text-slate-500 border-r border-slate-200">Teilnehmer · meine UE · Termine</div>
            <div className="flex">
              {(data.months || []).map((m) => (
                <div key={m.key} className="border-r border-slate-200 text-[10px] font-bold uppercase tracking-overline text-slate-600 px-1 py-2 truncate" style={{ width: m.days.length * CW }} data-testid={`overview-month-${m.key}`}>{m.label}</div>
              ))}
            </div>
          </div>

          <div className="flex border-b border-slate-200 bg-slate-50/60">
            <div className="w-[170px] sm:w-[300px] shrink-0 sticky left-0 bg-slate-50 z-10 px-4 py-1.5 text-[10px] uppercase tracking-overline text-slate-500 border-r border-slate-200">Tag · meine Kapazität</div>
            <div className="flex">
              {days.map((d) => (
                <div key={`c${d.date}`} className={`h-6 flex items-center justify-center text-[9px] text-slate-500 ${d.wd === 0 ? "border-l border-slate-300" : "border-r border-slate-100"} ${BG[d.state] || ""}`} style={{ width: CW }}
                  title={`${d.date}${d.holiday ? ` · ${d.holiday}` : ""}${d.state === "full" ? " · voll belegt" : d.state === "free" ? " · frei" : ""}`} data-testid={`overview-cap-${d.date}`}>
                  {d.dom}
                </div>
              ))}
            </div>
          </div>

          <div className="flex border-b border-slate-200 bg-white">
            <div className="w-[170px] sm:w-[300px] shrink-0 sticky left-0 bg-white z-10 px-4 py-1.5 text-[10px] uppercase tracking-overline text-slate-500 border-r border-slate-200">
              Termine am Tag <span className="normal-case tracking-normal text-slate-400">(max. {maxDay})</span>
            </div>
            <div className="flex">
              {days.map((d) => (
                <div key={`n${d.date}`} className={`h-7 flex items-center justify-center text-[11px] font-mono font-bold ${d.wd === 0 ? "border-l border-slate-300" : "border-r border-slate-100"} ${BG[d.state] || ""} ${!d.count ? "text-slate-300" : d.count > maxDay ? "text-rose-600" : d.count === maxDay ? "text-amber-600" : "text-slate-700"}`}
                  style={{ width: CW }} title={`${de(d.date)}: ${d.count || 0} Termine${d.count > maxDay ? " – mehr als in meine Arbeitszeit passen!" : ""}`} data-testid={`overview-daycount-${d.date}`}>
                  {d.count || "·"}
                </div>
              ))}
            </div>
          </div>

          {data.clients.length === 0 && <div className="px-5 py-8 text-xs text-slate-400">Keine aktiven Teilnehmer in diesem Zeitraum</div>}
          {data.clients.map((c) => {
            const byDay = {};
            c.items.forEach((x) => { (byDay[x.date] = byDay[x.date] || []).push(x); });
            const planned = (c.existing || 0) + (c.drafts || 0);
            const complete = c.required != null && planned >= c.required;
            return (
              <div key={c.client_id} className="flex border-b border-slate-100 hover:bg-slate-50/50 group" data-testid={`overview-row-${c.client_id}`}>
                <div className="w-[170px] sm:w-[300px] shrink-0 sticky left-0 bg-white group-hover:bg-slate-50 z-10 px-4 py-2 border-r border-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <a href={`/avgs/${c.client_id}`} className="text-sm font-medium text-slate-900 truncate hover:text-[#0a788c]" data-testid={`overview-name-${c.client_id}`}>{c.name}</a>
                    <span className={`text-[9px] px-1.5 py-0.5 shrink-0 ${complete ? "bg-emerald-50 text-emerald-800" : c.open ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-600"}`} data-testid={`overview-status-${c.client_id}`}>
                      {c.needs_count ? "Anzahl fehlt" : complete ? "komplett geplant" : `${c.open} offen`}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                    {c.my_ue ? `${Number(c.my_ue).toFixed(0)} UE` : "– UE"} · {planned}/{c.required ?? "?"} Termine · bis {c.end_date?.slice(8, 10)}.{c.end_date?.slice(5, 7)}.
                    {c.parts_total > 1 && <span className="text-slate-400"> · Coach {c.my_part}/{c.parts_total}</span>}
                  </div>
                  {Object.values(byDay).some((v) => v.length > 1) && (
                    <div className="text-[10px] text-rose-700 mt-0.5" data-testid={`overview-dup-${c.client_id}`}>
                      ⚠ {Object.values(byDay).filter((v) => v.length > 1).length}× zwei Termine am selben Tag
                    </div>
                  )}
                  {c.items.some((x) => x.outside) && (
                    <div className="text-[10px] text-rose-700 mt-0.5" data-testid={`overview-outside-${c.client_id}`}>
                      ⚠ {c.items.filter((x) => x.outside).length} Termin(e) außerhalb meiner Arbeitszeit
                    </div>
                  )}
                  {(c.plan_start || c.plan_end) && (
                    <div className="text-[10px] text-[#0a788c] mt-0.5" data-testid={`overview-window-${c.client_id}`}>
                      Mein Zeitraum: {de(c.plan_start) || "Start"} – {de(c.plan_end || c.end_date)}
                    </div>
                  )}
                  {edit === c.client_id ? (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`overview-window-edit-${c.client_id}`}>
                      <input type="date" value={win.from} min={c.start_date || undefined} max={c.end_date} onChange={(e) => setWin((w) => ({ ...w, from: e.target.value }))} className="h-6 border border-slate-300 text-[10px] px-1" data-testid={`overview-window-from-${c.client_id}`} />
                      <input type="date" value={win.to} min={c.start_date || undefined} max={c.end_date} onChange={(e) => setWin((w) => ({ ...w, to: e.target.value }))} className="h-6 border border-slate-300 text-[10px] px-1" data-testid={`overview-window-to-${c.client_id}`} />
                      <button onClick={() => saveWindow(c.client_id)} className="h-6 px-2 bg-slate-900 text-white text-[10px]" data-testid={`overview-window-save-${c.client_id}`}>Speichern</button>
                      <button onClick={() => setEdit("")} className="h-6 px-2 border border-slate-300 text-[10px]">Abbrechen</button>
                    </div>
                  ) : (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      <button onClick={() => plan(c.client_id)} disabled={busy === c.client_id || complete} className="h-6 px-2 border border-slate-300 text-[10px] hover:bg-slate-100 flex items-center gap-1 disabled:opacity-40" data-testid={`overview-plan-${c.client_id}`}><Wand2 size={10} /> Planen</button>
                      <button onClick={() => { setEdit(c.client_id); setWin({ from: c.my_from || "", to: c.my_to || "" }); }} className="h-6 px-2 border border-slate-300 text-[10px] hover:bg-slate-100 flex items-center gap-1" data-testid={`overview-window-btn-${c.client_id}`}><CalendarRange size={10} /> Zeitraum</button>
                      {c.unsent > 0 && <button onClick={() => send(c.client_id, c.name)} disabled={busy === c.client_id} className="h-6 px-2 border border-[#0a788c] text-[#0a788c] text-[10px] hover:bg-[#0a788c]/5 flex items-center gap-1 disabled:opacity-40" data-testid={`overview-send-${c.client_id}`}><Send size={10} /> {c.unsent} freigeben & senden</button>}
                    </div>
                  )}
                </div>
                <div className="flex">
                  {days.map((d) => {
                    const its = byDay[d.date];
                    const inRange = d.date >= (c.plan_start || c.start_date || "0000") && d.date <= (c.plan_end || c.end_date);
                    return (
                      <div key={c.client_id + d.date} className={`border-r border-slate-100 flex flex-col items-center justify-center gap-0.5 py-1 ${BG[d.state] || ""} ${inRange ? "" : "opacity-40"}`} style={{ width: CW, minHeight: 54 }}
                        title={its ? its.map((x) => `${de(d.date)} ${x.time_from}–${x.time_to} · ${SLABEL[x.status] || x.status}${x.ue ? ` · ${x.ue} UE` : ""}${x.outside ? " · ⚠ außerhalb meiner Arbeitszeit" : ""}`).join("\n") + (its.length > 1 ? `\n⚠ ${its.length} Termine am selben Tag` : "") : `${de(d.date)}${d.holiday ? ` · ${d.holiday}` : ""}`}
                        data-testid={its ? `overview-slot-${c.client_id}-${d.date}` : undefined}>
                        {(its || []).slice(0, 2).map((x) => (
                          <span key={x.id} className={`w-[23px] text-center rounded-sm text-[9px] font-mono font-semibold leading-[13px] ${CHIP[x.status] || "bg-slate-300"} ${x.outside ? "ring-2 ring-rose-500" : ""}`}>
                            {hhmm(x.time_from) || "–"}
                          </span>
                        ))}
                        {its && its.length > 2 && <span className="text-[8px] text-slate-500">+{its.length - 2}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="px-5 py-3 border-t border-slate-100"><Legend /></div>
    </div>
  );
}
